import type { SVGProps } from 'react';

/** Outline icon set for map tools — sourced from the Figma "Инструменты карты / Icons"
 * frame (node 148:43): 24×24, 2px stroke, round caps/joins. Color comes from
 * `currentColor` (set stroke color via the wrapping element's text color) so the
 * existing active/inactive button styling keeps working unchanged. */
type IconProps = SVGProps<SVGSVGElement>;

const base = (props: IconProps) => ({
  width: 24,
  height: 24,
  viewBox: '0 0 24 24',
  fill: 'none' as const,
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  ...props,
});

export function LegendIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M5 7.5C5.82843 7.5 6.5 6.82843 6.5 6C6.5 5.17157 5.82843 4.5 5 4.5C4.17157 4.5 3.5 5.17157 3.5 6C3.5 6.82843 4.17157 7.5 5 7.5Z" />
      <path d="M9 6H19" />
      <path d="M5 13.5C5.82843 13.5 6.5 12.8284 6.5 12C6.5 11.1716 5.82843 10.5 5 10.5C4.17157 10.5 3.5 11.1716 3.5 12C3.5 12.8284 4.17157 13.5 5 13.5Z" />
      <path d="M9 12H19" />
      <path d="M5 19.5C5.82843 19.5 6.5 18.8284 6.5 18C6.5 17.1716 5.82843 16.5 5 16.5C4.17157 16.5 3.5 17.1716 3.5 18C3.5 18.8284 4.17157 19.5 5 19.5Z" />
      <path d="M9 18H19" />
    </svg>
  );
}

export function ShareIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M18 8C19.6569 8 21 6.65685 21 5C21 3.34315 19.6569 2 18 2C16.3431 2 15 3.34315 15 5C15 6.65685 16.3431 8 18 8Z" />
      <path d="M6 15C7.65685 15 9 13.6569 9 12C9 10.3431 7.65685 9 6 9C4.34315 9 3 10.3431 3 12C3 13.6569 4.34315 15 6 15Z" />
      <path d="M18 22C19.6569 22 21 20.6569 21 19C21 17.3431 19.6569 16 18 16C16.3431 16 15 17.3431 15 19C15 20.6569 16.3431 22 18 22Z" />
      <path d="M8.6 10.6L15.4 6.5M8.6 13.4L15.4 17.5" />
    </svg>
  );
}

export function LayersIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M12 2L21 7L12 12L3 7L12 2Z" />
      <path d="M3 12L12 17L21 12" />
      <path d="M3 17L12 22L21 17" />
    </svg>
  );
}

export function BasemapIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M3 6L9 3L15 6L21 3V18L15 21L9 18L3 21V6Z" />
      <path d="M9 3V18M15 6V21" />
    </svg>
  );
}

export function ZoomInIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M11 18C14.866 18 18 14.866 18 11C18 7.13401 14.866 4 11 4C7.13401 4 4 7.13401 4 11C4 14.866 7.13401 18 11 18Z" />
      <path d="M20 20L16.5 16.5M11 8V14M8 11H14" />
    </svg>
  );
}

export function ZoomOutIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M11 18C14.866 18 18 14.866 18 11C18 7.13401 14.866 4 11 4C7.13401 4 4 7.13401 4 11C4 14.866 7.13401 18 11 18Z" />
      <path d="M20 20L16.5 16.5M8 11H14" />
    </svg>
  );
}

export function LocateIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" />
      <path d="M12 2V5M12 19V22M2 12H5M19 12H22" />
      <path d="M12 20C16.4183 20 20 16.4183 20 12C20 7.58172 16.4183 4 12 4C7.58172 4 4 7.58172 4 12C4 16.4183 7.58172 20 12 20Z" />
    </svg>
  );
}

export function HomeExtentIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M3 11L12 3L21 11" />
      <path d="M5 10V20H19V10" />
      <path d="M9 20V14H15V20" />
    </svg>
  );
}

export function FullscreenIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M8 3H3V8M16 3H21V8M8 21H3V16M16 21H21V16" />
    </svg>
  );
}

export function BoxSelectIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path
        d="M16 3H4C3.44772 3 3 3.44772 3 4V16C3 16.5523 3.44772 17 4 17H16C16.5523 17 17 16.5523 17 16V4C17 3.44772 16.5523 3 16 3Z"
        strokeDasharray="3 2"
      />
      <path d="M14 14L21 17L18 18L17 21L14 14Z" />
    </svg>
  );
}

export function ClearSelectionIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path
        d="M19 3H5C3.89543 3 3 3.89543 3 5V19C3 20.1046 3.89543 21 5 21H19C20.1046 21 21 20.1046 21 19V5C21 3.89543 20.1046 3 19 3Z"
        strokeDasharray="3 2"
      />
      <path d="M9 9L15 15M15 9L9 15" />
    </svg>
  );
}

export function MeasureDistanceIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M4 19L19 4" />
      <path d="M4 21C5.10457 21 6 20.1046 6 19C6 17.8954 5.10457 17 4 17C2.89543 17 2 17.8954 2 19C2 20.1046 2.89543 21 4 21Z" />
      <path d="M19 6C20.1046 6 21 5.10457 21 4C21 2.89543 20.1046 2 19 2C17.8954 2 17 2.89543 17 4C17 5.10457 17.8954 6 19 6Z" />
      <path d="M8 15L9.5 16.5M11 12L12.5 13.5M14 9L15.5 10.5" />
    </svg>
  );
}

export function MeasureAreaIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M5 5L18 7L20 18L8 20L4 12L5 5Z" />
      <path d="M5 6.5C5.82843 6.5 6.5 5.82843 6.5 5C6.5 4.17157 5.82843 3.5 5 3.5C4.17157 3.5 3.5 4.17157 3.5 5C3.5 5.82843 4.17157 6.5 5 6.5Z" />
      <path d="M18 8.5C18.8284 8.5 19.5 7.82843 19.5 7C19.5 6.17157 18.8284 5.5 18 5.5C17.1716 5.5 16.5 6.17157 16.5 7C16.5 7.82843 17.1716 8.5 18 8.5Z" />
      <path d="M20 19.5C20.8284 19.5 21.5 18.8284 21.5 18C21.5 17.1716 20.8284 16.5 20 16.5C19.1716 16.5 18.5 17.1716 18.5 18C18.5 18.8284 19.1716 19.5 20 19.5Z" />
      <path d="M8 21.5C8.82843 21.5 9.5 20.8284 9.5 20C9.5 19.1716 8.82843 18.5 8 18.5C7.17157 18.5 6.5 19.1716 6.5 20C6.5 20.8284 7.17157 21.5 8 21.5Z" />
      <path d="M4 13.5C4.82843 13.5 5.5 12.8284 5.5 12C5.5 11.1716 4.82843 10.5 4 10.5C3.17157 10.5 2.5 11.1716 2.5 12C2.5 12.8284 3.17157 13.5 4 13.5Z" />
    </svg>
  );
}

export function TerritoryCalculatorIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M10 3H5C3.89543 3 3 3.89543 3 5V19C3 20.1046 3.89543 21 5 21H10C11.1046 21 12 20.1046 12 19V5C12 3.89543 11.1046 3 10 3Z" />
      <path d="M5.5 7H9.5M6 11H6.01M9 11H9.01M6 15H6.01M9 15H9.01" />
      <path d="M15 8L20 10L19 18L13 20L14 13L15 8Z" />
    </svg>
  );
}

export function CoordinatesIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" />
      <path d="M12 2V7M12 17V22M2 12H7M17 12H22" />
      <path d="M7 4H4V7M17 20H20V17" />
    </svg>
  );
}

export function DrawIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M4 20H8L19 9C19.5304 8.46957 19.8284 7.75015 19.8284 7C19.8284 6.24985 19.5304 5.53043 19 5C18.4696 4.46957 17.7501 4.17157 17 4.17157C16.2499 4.17157 15.5304 4.46957 15 5L4 16V20Z" />
      <path d="M13.5 6.5L17.5 10.5M4 16L8 20" />
    </svg>
  );
}

export function EditFeaturesIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M4 6L10 3L15 7L13 13L6 15L4 6Z" />
      <path d="M4 7.5C4.82843 7.5 5.5 6.82843 5.5 6C5.5 5.17157 4.82843 4.5 4 4.5C3.17157 4.5 2.5 5.17157 2.5 6C2.5 6.82843 3.17157 7.5 4 7.5Z" />
      <path d="M10 4.5C10.8284 4.5 11.5 3.82843 11.5 3C11.5 2.17157 10.8284 1.5 10 1.5C9.17157 1.5 8.5 2.17157 8.5 3C8.5 3.82843 9.17157 4.5 10 4.5Z" />
      <path d="M15 8.5C15.8284 8.5 16.5 7.82843 16.5 7C16.5 6.17157 15.8284 5.5 15 5.5C14.1716 5.5 13.5 6.17157 13.5 7C13.5 7.82843 14.1716 8.5 15 8.5Z" />
      <path d="M13 19L19 13L21 15L15 21L12 22L13 19Z" />
    </svg>
  );
}

export function DeleteSelectionIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M5 7H19M9 7V4H15V7M8 7L9 20H15L16 7" />
      <path d="M6 3H3V6M18 21H21V18" />
    </svg>
  );
}

export function SpatialQueryIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M12 4H4C3.44772 4 3 4.44772 3 5V13C3 13.5523 3.44772 14 4 14H12C12.5523 14 13 13.5523 13 13V5C13 4.44772 12.5523 4 12 4Z" />
      <path d="M18 10H10C9.44772 10 9 10.4477 9 11V19C9 19.5523 9.44772 20 10 20H18C18.5523 20 19 19.5523 19 19V11C19 10.4477 18.5523 10 18 10Z" />
      <path d="M17 9C18.6569 9 20 7.65685 20 6C20 4.34315 18.6569 3 17 3C15.3431 3 14 4.34315 14 6C14 7.65685 15.3431 9 17 9Z" />
      <path d="M19 8L21 10" />
    </svg>
  );
}

export function ExportSelectionIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path
        d="M14 3H4C3.44772 3 3 3.44772 3 4V14C3 14.5523 3.44772 15 4 15H14C14.5523 15 15 14.5523 15 14V4C15 3.44772 14.5523 3 14 3Z"
        strokeDasharray="3 2"
      />
      <path d="M14 14H21V21H14" />
      <path d="M17.5 10V18M20.5 15L17.5 18L14.5 15" />
    </svg>
  );
}

export function PrintMapIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M6 9V3H18V9" />
      <path d="M18 14H6V21H18V14Z" />
      <path d="M6 17H4C3.46957 17 2.96086 16.7893 2.58579 16.4142C2.21071 16.0391 2 15.5304 2 15V11C2 10.4696 2.21071 9.96086 2.58579 9.58579C2.96086 9.21071 3.46957 9 4 9H20C20.5304 9 21.0391 9.21071 21.4142 9.58579C21.7893 9.96086 22 10.4696 22 11V15C22 15.5304 21.7893 16.0391 21.4142 16.4142C21.0391 16.7893 20.5304 17 20 17H18" />
      <path d="M18 12H18.01" />
    </svg>
  );
}

export function AiAnalysisIcon(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M4 8L11 4L19 8L17 17L9 20L4 15V8Z" />
      <path d="M15 2L15.8 4.2L18 5L15.8 5.8L15 8L14.2 5.8L12 5L14.2 4.2L15 2Z" />
      <path d="M19 13L19.6 14.4L21 15L19.6 15.6L19 17L18.4 15.6L17 15L18.4 14.4L19 13Z" />
    </svg>
  );
}
